import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
} from "@angular/common/http";
import { Observable, of } from "rxjs";
import { delay } from "rxjs/operators";
import { environment } from "../../../environments/environment";

@Injectable()
export class MockApiInterceptor implements HttpInterceptor {
  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    console.log(
      "[MockApiInterceptor] Intercepting request:",
      req.url,
      "Method:",
      req.method,
    );
    console.log(
      "[MockApiInterceptor] useMockApi:",
      environment.features.useMockApi,
    );

    if (!environment.features.useMockApi) {
      console.log(
        "[MockApiInterceptor] Mock API disabled, passing to next handler",
      );
      return next.handle(req);
    }

    // IMPORTANT: Check specific patterns BEFORE general patterns
    // Order matters here - most specific first!

    // 0. Filter options (billing entities, agents, etc.)
    // 1. Most specific: invocation sub-resources (/invocations/{id}/events, /invocations/{id}/audio-url, /invocations/{id}/logs)
    // 2. Specific: single invocation (/invocations/{id})
    // 3. General: invocations list (/invocations)
    // 4. General: charts (/charts/...)

    // Mock billing entities filter options
    if (req.url.includes("/filters/billing-entities") && req.method === "GET") {
      console.log("[MockApiInterceptor] Matched billing-entities endpoint!");
      return of(
        new HttpResponse({
          status: 200,
          body: {
            data: [
              {
                billingEntityId: "clinic-001",
                billingEntityType: "clinic",
                displayName: "Main Street Clinic",
              },
              {
                billingEntityId: "clinic-002",
                billingEntityType: "clinic",
                displayName: "Downtown Health Center",
              },
              {
                billingEntityId: "hospital-001",
                billingEntityType: "hospital",
                displayName: "Regional Medical",
              },
              {
                billingEntityId: "agency-001",
                billingEntityType: "agency",
                displayName: "Home Health Partners",
              },
            ],
          },
        }),
      ).pipe(delay(200));
    }

    // Mock invocation events/timeline (most specific)
    if (
      req.url.match(/\/invocations\/[^/]+\/events$/) &&
      req.method === "GET"
    ) {
      console.log("[MockApiInterceptor] Matched events endpoint!");
      const invocationId = req.url.split("/")[req.url.split("/").length - 2];
      const mockEvents = this.generateMockEvents(invocationId);
      console.log("[MockApiInterceptor] Returning mock events:", mockEvents);
      return of(
        new HttpResponse({
          status: 200,
          body: {
            events: mockEvents,
          },
        }),
      ).pipe(delay(400));
    }

    // Mock audio URL (most specific)
    if (
      req.url.match(/\/invocations\/[^/]+\/audio-url$/) &&
      req.method === "GET"
    ) {
      console.log("[MockApiInterceptor] Matched audio-url endpoint!");
      return of(
        new HttpResponse({
          status: 200,
          body: {
            url: "https://example.com/mock-audio.mp3",
            expiresAt: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
          },
        }),
      ).pipe(delay(300));
    }

    // Mock logs (most specific)
    if (req.url.match(/\/invocations\/[^/]+\/logs$/) && req.method === "GET") {
      console.log("[MockApiInterceptor] Matched logs endpoint!");
      const invocationId = req.url.split("/")[req.url.split("/").length - 2];
      const mockLogs = this.generateMockLogs(invocationId);
      console.log("[MockApiInterceptor] Returning mock logs:", mockLogs);
      return of(
        new HttpResponse({
          status: 200,
          body: {
            entries: mockLogs,
            nextPageToken: null,
          },
        }),
      ).pipe(delay(400));
    }

    // Mock invocation detail (specific - single resource)
    if (req.url.match(/\/invocations\/[^/]+$/) && req.method === "GET") {
      console.log("[MockApiInterceptor] Matched invocation detail endpoint!");
      const invocationId = req.url.split("/").pop();
      const mockDetail = this.generateMockInvocationDetail(invocationId!);
      console.log(
        "[MockApiInterceptor] Returning mock invocation detail:",
        mockDetail,
      );
      return of(
        new HttpResponse({
          status: 200,
          body: mockDetail,
        }),
      ).pipe(delay(300));
    }

    // Mock invocations list (general - collection)
    if (req.url.includes("/invocations") && req.method === "GET") {
      console.log("[MockApiInterceptor] Matched invocations list endpoint!");
      const page = parseInt(req.params.get("page") || "1", 10);
      const pageSize = parseInt(req.params.get("pageSize") || "25", 10);
      const totalItems = 100;
      const totalPages = Math.ceil(totalItems / pageSize);

      const mockData = this.generateMockInvocations(pageSize, page);
      return of(
        new HttpResponse({
          status: 200,
          body: {
            data: mockData,
            pagination: {
              page,
              pageSize,
              totalItems,
              totalPages,
            },
          },
        }),
      ).pipe(delay(500));
    }

    // Mock chart data - invocations over time
    if (req.url.includes("/charts/invocations-over-time")) {
      return of(
        new HttpResponse({
          status: 200,
          body: {
            data: this.generateMockTimeSeriesData(),
          },
        }),
      ).pipe(delay(500));
    }

    // Mock chart data - completion status
    if (req.url.includes("/charts/completion-status")) {
      return of(
        new HttpResponse({
          status: 200,
          body: {
            data: [
              { status: "completed", count: 145 },
              { status: "in_progress", count: 23 },
              { status: "failed", count: 12 },
            ],
          },
        }),
      ).pipe(delay(500));
    }

    // Mock chart data - duration histogram
    if (req.url.includes("/charts/duration-histogram")) {
      return of(
        new HttpResponse({
          status: 200,
          body: {
            data: [
              { bucketStart: 0, bucketEnd: 30, count: 45 },
              { bucketStart: 30, bucketEnd: 60, count: 78 },
              { bucketStart: 60, bucketEnd: 90, count: 56 },
              { bucketStart: 90, bucketEnd: 120, count: 34 },
              { bucketStart: 120, bucketEnd: 150, count: 21 },
            ],
          },
        }),
      ).pipe(delay(500));
    }

    console.log(
      "[MockApiInterceptor] No pattern matched, passing to next handler",
    );
    return next.handle(req);
  }

  private generateMockInvocations(count: number, page: number = 1): any[] {
    const results = ["answered", "no_answer", "voicemail", "error", null];
    const statuses = ["completed", "in_progress", "failed", null];
    const offset = (page - 1) * count;

    return Array.from({ length: count }, (_, i) => {
      const index = offset + i;
      return {
        invocationId: `inv-${String(index + 1).padStart(4, "0")}`,
        createdAt: new Date(Date.now() - index * 3600000).toISOString(),
        phoneNumber: `+1555${String(index).padStart(7, "0")}`,
        result: results[index % results.length],
        billingEntityType: "clinic",
        billingEntityId: `clinic-${index % 10}`,
        agentId: `agent-${index % 3}`,
        completionStatus: statuses[index % statuses.length],
        durationMs: 30000 + ((index * 12345) % 270000),
        hasRecording: index % 5 !== 0,
      };
    });
  }

  private generateMockInvocationDetail(invocationId: string): any {
    return {
      invocationId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      agentId: "agent-1",
      phoneNumber: "+15551234567",
      channel: {
        channel: "phone",
        toPhoneNumber: "+15551234567",
        fromPhoneNumber: "+15559876543",
      },
      context: {
        patientName: "John Doe",
        appointmentDate: "2026-02-10",
        reason: "Follow-up call",
      },
      billingEntityType: "clinic",
      billingEntityId: "clinic-123",
      invokeAt: null,
      invokeBy: null,
      isCancelled: false,
      isOnDemand: true,
      result: "answered",
      transcript: [
        {
          role: "assistant",
          content: "Hello, this is a reminder call for your appointment.",
          timestamp: new Date().toISOString(),
        },
        {
          role: "user",
          content: "Yes, I remember. Thank you!",
          timestamp: new Date().toISOString(),
        },
      ],
      durationMs: 125000,
      completionStatus: "completed",
    };
  }

  private generateMockTimeSeriesData(): any[] {
    return Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (29 - i));
      return {
        timestamp: date.toISOString(),
        count: Math.floor(Math.random() * 100) + 20,
      };
    });
  }

  private generateMockEvents(invocationId: string): any[] {
    const baseTime = new Date();
    const events = [];

    // Invocation start
    events.push({
      dateTime: new Date(baseTime.getTime() - 125000).toISOString(),
      eventType: "invocation_start",
      payload: {
        invocationId,
        agentId: "agent-1",
      },
    });

    // Call started
    events.push({
      dateTime: new Date(baseTime.getTime() - 120000).toISOString(),
      eventType: "call_started",
      payload: {
        phoneNumber: "+15551234567",
      },
    });

    // Assistant message
    events.push({
      dateTime: new Date(baseTime.getTime() - 115000).toISOString(),
      eventType: "message",
      payload: {
        role: "assistant",
        textContent:
          "Hello, this is a reminder call for your appointment on February 10th at 2:00 PM. Can you confirm your attendance?",
      },
    });

    // User message
    events.push({
      dateTime: new Date(baseTime.getTime() - 110000).toISOString(),
      eventType: "message",
      payload: {
        role: "user",
        textContent: "Yes, I'll be there. Thank you for the reminder!",
      },
    });

    // Tool call
    events.push({
      dateTime: new Date(baseTime.getTime() - 105000).toISOString(),
      eventType: "tool_call",
      payload: {
        id: "call_abc123",
        tool: "update_appointment_status",
        arguments: {
          appointmentId: "appt-456",
          status: "confirmed",
        },
        result: {
          success: true,
          message: "Appointment confirmed",
        },
      },
    });

    // Another assistant message
    events.push({
      dateTime: new Date(baseTime.getTime() - 100000).toISOString(),
      eventType: "message",
      payload: {
        role: "assistant",
        textContent:
          "Perfect! Your appointment is confirmed. Is there anything else I can help you with?",
      },
    });

    // User message
    events.push({
      dateTime: new Date(baseTime.getTime() - 95000).toISOString(),
      eventType: "message",
      payload: {
        role: "user",
        textContent: "No, that's all. Thank you!",
      },
    });

    // Call ended
    events.push({
      dateTime: new Date(baseTime.getTime() - 5000).toISOString(),
      eventType: "call_ended",
      payload: {
        duration: 125000,
        result: "answered",
      },
    });

    // Invocation complete
    events.push({
      dateTime: baseTime.toISOString(),
      eventType: "invocation_complete",
      payload: {
        status: "completed",
        duration: 125000,
      },
    });

    return events;
  }

  private generateMockLogs(invocationId: string): any[] {
    const baseTime = new Date();
    const severities = ["DEBUG", "INFO", "WARNING", "ERROR"];
    const logs = [];

    // Generate various log entries
    logs.push({
      timestamp: new Date(baseTime.getTime() - 125000).toISOString(),
      severity: "INFO",
      message: `Invocation ${invocationId} started`,
      jsonPayload: {
        invocationId,
        agentId: "agent-1",
        channel: "phone",
      },
    });

    logs.push({
      timestamp: new Date(baseTime.getTime() - 120000).toISOString(),
      severity: "DEBUG",
      message: "Initializing telephony connection",
      jsonPayload: {
        provider: "twilio",
        phoneNumber: "+15551234567",
      },
    });

    logs.push({
      timestamp: new Date(baseTime.getTime() - 115000).toISOString(),
      severity: "INFO",
      message: "Call connected successfully",
      jsonPayload: {
        callSid: "CA1234567890abcdef",
        duration: 0,
      },
    });

    logs.push({
      timestamp: new Date(baseTime.getTime() - 110000).toISOString(),
      severity: "DEBUG",
      message: "Processing user speech input",
      jsonPayload: {
        transcript: "Yes, I'll be there. Thank you for the reminder!",
        confidence: 0.95,
      },
    });

    logs.push({
      timestamp: new Date(baseTime.getTime() - 105000).toISOString(),
      severity: "INFO",
      message: "Tool call executed: update_appointment_status",
      jsonPayload: {
        tool: "update_appointment_status",
        arguments: {
          appointmentId: "appt-456",
          status: "confirmed",
        },
        result: {
          success: true,
        },
      },
    });

    logs.push({
      timestamp: new Date(baseTime.getTime() - 100000).toISOString(),
      severity: "WARNING",
      message: "Speech synthesis latency high",
      jsonPayload: {
        latencyMs: 850,
        threshold: 500,
      },
    });

    logs.push({
      timestamp: new Date(baseTime.getTime() - 5000).toISOString(),
      severity: "INFO",
      message: "Call ended by user",
      jsonPayload: {
        duration: 125000,
        result: "answered",
      },
    });

    logs.push({
      timestamp: baseTime.toISOString(),
      severity: "INFO",
      message: `Invocation ${invocationId} completed successfully`,
      jsonPayload: {
        status: "completed",
        totalDuration: 125000,
        messageCount: 4,
        toolCalls: 1,
      },
    });

    return logs;
  }
}
